/**
 * Step + FAQ copy and JSON-LD for /how-it-works.
 *
 * Text-only (icons are mapped in the page by `iconKey`) so the route file can
 * build head metadata without bundling the page component.
 */
export const STEPS = [
  {
    iconKey: "ScanLine",
    title: "GSTIN, PAN & MCA verification",
    body:
      "Every yard, foundry and trader is auto-verified against the live GSTN, PAN and MCA databases. Legal name, registration status, principal place of business and filing history are checked in real time. Penny-drop bank verification matches the beneficiary name before any payout. Only GST-verified scrap dealers and verified sponge iron, MS billet and ferro-alloy suppliers are allowed to transact.",
  },
  {
    iconKey: "Brain",
    title: "AI-benchmarked pricing & quality grading",
    body:
      "Our pricing engine ingests live mill, mandi and import-parity data to publish AI-benchmarked rates for HMS 1, HMS 1 & 2 (80:20), shredded steel, sponge iron, MS billets, pig iron, dolomite, ferro-manganese, silico-manganese and more. Yard photos are graded by a computer-vision model so buyers see a fair, objective quality score before committing.",
  },
  {
    iconKey: "Banknote",
    title: "RBI-grade nodal escrow",
    body:
      "Buyer funds are locked in an RBI-compliant nodal escrow account at a partner bank before dispatch. Money never touches LohaLink's books. Escrow releases automatically to the seller after the buyer confirms inbound weighbridge and quality — eliminating bad debt, post-dated cheques (PDCs) and letters of credit.",
  },
  {
    iconKey: "Truck",
    title: "Pan-India logistics & live truck tracking",
    body:
      "Integrated logistics partners cover Durgapur, Kolkata, Jharsuguda, Raipur, Asansol, Mandi Gobindgarh, Jalna, Alang, Ghaziabad and every other major steel hub. Every consignment ships with a digital LR, geotagged loading photos and a tamper-evident driver tracking link that pings location on a configurable interval. Counterparties get email and dashboard updates throughout transit.",
  },
  {
    iconKey: "Scale",
    title: "Digital weighbridge & dharam kanta receipts",
    body:
      "Outbound and inbound weighbridge slips are uploaded as tamper-evident dharam kanta receipts. The platform automatically reconciles outbound vs inbound weight, flags variances beyond the agreed tolerance, and produces a digital audit trail for every truck.",
  },
  {
    iconKey: "Gavel",
    title: "Dispute resolution in 24–48 hours",
    body:
      "If quality or weight is contested, LohaLink's dispute team reviews the AI grading report, weighbridge slips and geotagged photos and mediates a fair settlement, adjusting the escrow release. Most disputes close within 24–48 hours — far faster than the weeks it takes through traditional brokers.",
  },
  {
    iconKey: "FileText",
    title: "GST e-invoicing & e-way bills",
    body:
      "Tax-compliant GST invoices and e-way bills are auto-generated for every trade, with IRN and QR codes, and pushed directly into both parties' accounting systems and admin dashboards.",
  },
  {
    iconKey: "Bell",
    title: "Notify-me & material expansion engine",
    body:
      "Buyers can register interest in upcoming materials — iron ore, iron pellets, ingots, angles, billets, TMT bars, coal, quartzite and ferro-alloys. A confirmation email goes out instantly, the request is logged in admin, and a notification is fired the moment the category goes live.",
  },
  {
    iconKey: "Network",
    title: "Direct foundry network — no middlemen",
    body:
      "LohaLink connects 3,400+ GST-verified scrap yards directly with foundries, rolling mills and EAF/induction units. Removing middle layers means better realisation for sellers, lower landed cost for buyers, and a transparent paper trail at every step.",
  },
  {
    iconKey: "Recycle",
    title: "Circular-economy compliance",
    body:
      "Every trade is logged against the MoEFCC Steel Scrap Recycling Policy framework, supporting India's circular-economy goals and giving large buyers the documentation they need for ESG and Scope-3 emissions reporting.",
  },
];
export const FAQS = [
  {
    q: "What happens behind the scenes from order to delivery on LohaLink?",
    a: "Buyer and seller are KYC + GSTIN verified, an AI-benchmarked price is locked, funds move into nodal escrow, the seller dispatches with geotagged photos and a live tracking link, weighbridge slips are reconciled digitally, and escrow releases to the seller within hours of buyer acceptance.",
  },
  {
    q: "Is LohaLink an RBI-regulated escrow?",
    a: "LohaLink operates on a nodal escrow account at a regulated partner bank under RBI guidelines. Funds never sit on LohaLink's books and are released only against verified weighbridge and quality acceptance.",
  },
  {
    q: "Which materials can I buy or sell on LohaLink today?",
    a: "Live categories include HMS 1, HMS 1 & 2 (80:20), shredded steel, CI scrap, MS turning scrap, sponge iron (DRI), MS billets and pig iron. Iron ore, iron pellets, ingots, angle, TMT bars, coal, dolomite, quartzite, ferro-manganese and silico-manganese are coming soon — register on the Notify Me form to be alerted at launch.",
  },
  {
    q: "How does AI scrap quality grading work?",
    a: "Sellers upload yard and loading photos. A computer-vision model classifies the lot against reference grades (HMS 1, HMS 1 & 2, shredded, turnings, sponge iron lumps, etc.), flags contamination, and publishes a quality score that both sides see before locking the trade.",
  },
  {
    q: "What if the inbound weight differs from the outbound weight?",
    a: "Variances beyond the agreed tolerance trigger an automated hold on escrow release. The dispute team reviews the dharam kanta receipts, AI grading report and geotagged photos and mediates a fair settlement — typically within 24–48 hours.",
  },
  {
    q: "Does LohaLink handle GST invoicing and e-way bills?",
    a: "Yes. Tax-compliant GST invoices with IRN and QR, plus e-way bills, are auto-generated for every trade and made available to both parties in their dashboards.",
  },
];
export const HOWTO_LD = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How a bulk scrap metal trade works on LohaLink",
  description:
    "Step-by-step view of what LohaLink does in the background — verification, AI pricing, escrow, logistics, weighbridge reconciliation, dispute resolution and GST e-invoicing.",
  step: STEPS.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.title,
    text: s.body,
  })),
};

export const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export const BREADCRUMB_LD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://lohalink.in/" },
    {
      "@type": "ListItem",
      position: 2,
      name: "How LohaLink Works",
      item: "https://lohalink.in/how-it-works",
    },
  ],
};
